'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface BrandsStats {
  totalBrands: number;
  totalReviews: number;
}

export default function BrandsHero() {
  const [stats, setStats] = useState<BrandsStats>({ totalBrands: 0, totalReviews: 0 });

  useEffect(() => {
    async function fetchStats() {
      const supabase = createClient();
      try {
        const [brandsRes, reviewsRes] = await Promise.all([
          supabase.from('campaigns').select('seller_id'),
          supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('review_status', 'approved'),
        ]);

        const uniqueBrands = new Set(brandsRes.data?.map((c) => c.seller_id) || []).size;
        setStats({
          totalBrands: uniqueBrands || 0,
          totalReviews: reviewsRes.count || 0,
        });
      } catch (err) {
        console.log('Brands stats fetch error:', err);
      }
    }
    fetchStats();
  }, []);

  const statCards = [
    { label: 'Brands Served', value: stats.totalBrands > 0 ? `${stats.totalBrands}+` : '—', icon: 'BuildingStorefrontIcon', color: '#FF5722', bg: '#FFF3EE' },
    { label: 'Avg. Campaign Rating', value: '4.4★', icon: 'StarIcon', color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'Reviews Collected', value: stats.totalReviews > 0 ? `${(stats.totalReviews / 1000).toFixed(0)}K+` : '—', icon: 'ChatBubbleLeftRightIcon', color: '#10B981', bg: '#ECFDF5' },
    { label: 'Avg. Review Quality', value: '91%', icon: 'SparklesIcon', color: '#8B5CF6', bg: '#F5F3FF' },
  ];

  return (
    <section className="relative pt-16 overflow-hidden bg-white">
      {/* Dot grid */}
      <div className="absolute inset-0 dot-grid-bg opacity-50 pointer-events-none" />

      {/* Color blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-8 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(26,35,126,0.08) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-6 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,87,34,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-2 bg-secondary-container rounded-full px-4 py-2 border border-secondary/15">
              <Icon name="BuildingStorefrontIcon" size={14} className="text-secondary" />
              <span className="text-xs font-bold text-secondary uppercase tracking-widest">For Brands & Businesses</span>
            </div>
            <h1 className="font-heading font-bold text-foreground" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: 1.1 }}>
              Get Your Products
              <br />
              <span className="gradient-text">Reviewed & Tested</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">
              Submit your products to verified Indian creators for honest reviews, testing, and feedback. Every review is AI quality-checked for authenticity.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/contact" className="cta-gradient text-white font-heading font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity text-sm shadow-lg shadow-primary/20">
                Submit a Product
              </Link>
              <Link href="#roi-calculator" className="border border-border text-foreground font-heading font-semibold px-8 py-4 rounded-xl hover:bg-muted transition-all text-sm">
                Calculate ROI
              </Link>
            </div>
            <div className="flex flex-wrap gap-6 pt-2">
              {[
                { icon: 'ShieldCheckIcon', text: 'AI Quality Check' },
                { icon: 'DocumentTextIcon', text: 'GST Invoices' },
                { icon: 'ChartBarIcon', text: 'Real-time Dashboard' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2 text-muted-foreground">
                  <Icon name={b.icon as 'ShieldCheckIcon'} size={15} className="text-primary" />
                  <span className="text-xs font-medium">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {statCards.map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl p-5 border border-border shadow-sm card-hover">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: stat.bg }}>
                  <Icon name={stat.icon as 'BuildingStorefrontIcon'} size={20} style={{ color: stat.color }} />
                </div>
                <div className="font-heading font-bold text-2xl text-foreground">{stat.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #F7F8FA)' }} />
    </section>
  );
}